import { ReactElement, useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { Check } from '@mui/icons-material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  AssignmentIcon,
  CorrectIcon,
  CourseIcon,
  IncorrectIcon,
  SearchIcon,
  UserIcon,
} from 'src/v3/core/icons'
import {
  fetchAssignmentDetailV4,
  fetchAllCourseUserProgressV4,
  fetchLinkedServices,
  selectActiveAssignmentDetailV4,
  selectActiveAssignmentDetailV4Status,
  selectCourseUserProgressV4,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { SelectedDataForCredit } from './types'
import { dataGridSx, paperInputSx } from '../styles'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import BulkCourseCreditModal from './components/modals/bulk-course-credit-modal'
import { giveCourseCredit } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import { getIsOverdue } from './utils/helpers'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { ASSIGNMENTS_URLS } from '../constants'

dayjs.extend(utc)

function CourseProgressSkeleton(): ReactElement {
  return (
    <Card sx={{ bgcolor: 'background.paper' }} className="p-6 mb-6">
      <Box className="flex items-start justify-between gap-4 mb-4">
        <Box className="flex-1">
          <Box className="flex items-center gap-1 mb-2">
            <Skeleton variant="circular" width={18} height={18} />
            <Skeleton variant="text" width="60%" height={28} />
          </Box>
          <Box className="flex items-center gap-1 mb-2">
            <Skeleton variant="circular" width={16} height={16} />
            <Skeleton variant="text" width="50%" height={20} />
          </Box>
        </Box>
      </Box>

      <Box className="mb-4">
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
      </Box>

      <Box className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Paper className="p-4" sx={{ bgcolor: 'background.paperDark' }}>
          <Skeleton variant="text" width={200} height={32} className="mb-3" />
          <Skeleton
            variant="rectangular"
            height={400}
            sx={{ borderRadius: 1 }}
          />
        </Paper>
        <Paper className="p-4" sx={{ bgcolor: 'background.paperDark' }}>
          <Skeleton variant="text" width={200} height={32} className="mb-3" />
          <Skeleton
            variant="rectangular"
            height={400}
            sx={{ borderRadius: 1 }}
          />
        </Paper>
      </Box>
    </Card>
  )
}

export default function CourseProgress(): ReactElement {
  useScrollToTop()
  let { assignmentID, courseID } = useParams<{
    assignmentID: string
    courseID: string
  }>()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let [searchQuery, setSearchQuery] = useState('')
  let [isLoadingCourseUsers, setIsLoadingCourseUsers] = useState<boolean>(false)
  let [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  let [courseCreditModalOpen, setCourseCreditModalOpen] = useState(false)
  let [selectedDataForCredit, setSelectedDataForCredit] =
    useState<SelectedDataForCredit | null>(null)
  let [selectedUserIDs, setSelectedUserIDs] = useState<string[]>([])

  let CourseProgressToolbar = useCallback(
    () => <DataGridToolbar fileName="course-progress" />,
    [],
  )

  let assignment = useAppSelector(selectActiveAssignmentDetailV4)
  let assignmentDetailStatus = useAppSelector(
    selectActiveAssignmentDetailV4Status,
  )
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let allCourseUserProgress = useAppSelector(selectCourseUserProgressV4)

  let selectedCourse = assignment?.courses.find((c) => c.id === courseID)
  let courseUserProgressKey = `${assignment?.id}-${courseID}`
  let courseUserProgress = courseUserProgressKey
    ? allCourseUserProgress[courseUserProgressKey] || []
    : []
  let successMessage = `Successfully gave course credit for ${selectedCourse?.name}.`
  let errorMessage = `Error giving user course credit for ${selectedCourse?.name}.`

  let linkedServiceName = useLinkedServiceName(assignment?.linkedServiceID)

  useEffect(() => {
    if (
      assignmentID &&
      (assignmentDetailStatus === 'uninitialized' ||
        assignment?.id !== assignmentID)
    ) {
      dispatch(fetchAssignmentDetailV4(assignmentID))
    }
  }, [assignmentID, assignmentDetailStatus, assignment?.id, dispatch])

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    async function loadCourseUserProgress() {
      if (assignmentID && courseID) {
        setIsLoadingCourseUsers(true)
        try {
          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
          await dispatch(
            fetchAllCourseUserProgressV4({
              assignmentId: assignmentID,
              courseId: courseID,
            }),
          ).unwrap()
        } finally {
          setIsLoadingCourseUsers(false)
        }
      }
    }

    loadCourseUserProgress()
  }, [assignmentID, courseID, dispatch])

  let handleGiveCourseCredit = async () => {
    if (!assignmentID || !courseID || !selectedDataForCredit?.userId) return

    setIsSubmitting(true)

    let payload = {
      assignmentID,
      userIDs: [selectedDataForCredit.userId],
      courseIDs: [selectedDataForCredit.courseId],
    }

    await giveCourseCredit(payload)
      .then(() =>
        Promise.all([
          dispatch(
            fetchAllCourseUserProgressV4({
              assignmentId: assignmentID,
              courseId: courseID,
            }),
          ),
          dispatch(fetchAssignmentDetailV4(assignmentID)),
        ]),
      )
      .then(() => showSuccess(successMessage))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(errorMessage, 'Payload: ', payload, 'Error: ', err)
        showError(errorMessage)
      })
      .finally(() => {
        setCourseCreditModalOpen(false)
        setSelectedDataForCredit(null)
        setIsSubmitting(false)
      })
  }

  let handleGiveBulkCourseCredit = async () => {
    if (!assignmentID || !courseID || selectedUserIDs.length === 0) return

    setIsSubmitting(true)

    let payload = {
      assignmentID,
      userIDs: selectedUserIDs,
      courseIDs: [courseID],
    }

    await giveCourseCredit(payload)
      .then(() =>
        Promise.all([
          dispatch(
            fetchAllCourseUserProgressV4({
              assignmentId: assignmentID,
              courseId: courseID,
            }),
          ),
          dispatch(fetchAssignmentDetailV4(assignmentID)),
        ]),
      )
      .then(() => showSuccess(successMessage))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(errorMessage, 'Payload: ', payload, 'Error: ', err)
        showError(errorMessage)
      })
      .finally(() => {
        setSelectedUserIDs([])
        setIsSubmitting(false)
      })
  }

  let isLoading = assignmentDetailStatus === 'pending' || isLoadingCourseUsers
  let isOverdue = getIsOverdue(assignment?.dueDate || '')

  let filteredUsers = courseUserProgress.filter(
    (user) =>
      user.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  let completedUsers = filteredUsers.filter(
    (user) => user.courseStatus === 'Completed',
  )
  let notCompletedUsers = filteredUsers.filter(
    (user) => user.courseStatus === 'NotStarted',
  )

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="mb-4">
        <PageTitleWithLinkedService
          pageTitle="Course Progress"
          linkedServiceName={linkedServiceName}
          moduleName="Assignments"
          handleBackClick={() =>
            navigate(`${ASSIGNMENTS_URLS.INDEX}/${assignmentID}`)
          }
          isLoading={isLoading}
        />
      </Box>
      {isLoading ? (
        <CourseProgressSkeleton />
      ) : (
        <Card sx={{ bgcolor: 'background.paper' }} className="p-6">
          <Box className="flex items-start justify-between gap-4 mb-4">
            <Box className="flex-1">
              <Box className="flex items-center gap-1 mb-2">
                <AssignmentIcon
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                  aria-hidden="true"
                />
                <Typography
                  variant="subtitle2"
                  component="h3"
                  color="text.secondary"
                >
                  {assignment?.name || 'Unknown Assignment'}
                </Typography>
              </Box>
              <Box className="flex items-center gap-2 mb-2">
                <CourseIcon
                  sx={{ fontSize: 18, color: 'text.secondary' }}
                  aria-hidden="true"
                />
                <Typography variant="h6" component="h2">
                  {selectedCourse?.name || 'Unknown Course'}
                </Typography>
              </Box>
            </Box>
            {isOverdue && (
              <Chip
                label="Overdue"
                color="error"
                size="medium"
                icon={<IncorrectIcon aria-hidden="true" />}
              />
            )}
          </Box>

          <Box className="mb-4">
            <TextField
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="medium"
              fullWidth
              slotProps={{
                input: {
                  sx: { ...paperInputSx, backgroundColor: 'background.paper' },
                  startAdornment: (
                    <SearchIcon
                      className="mr-2"
                      sx={{ color: 'text.secondary' }}
                      aria-hidden="true"
                    />
                  ),
                },
              }}
            />
          </Box>

          <Box className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Box>
              <Box
                className="flex items-center gap-2 mb-3"
                sx={{ minHeight: 38 }}
              >
                <CorrectIcon
                  sx={{ fontSize: 20, color: 'success.main' }}
                  aria-hidden="true"
                />
                <Typography color="success.main" variant="h6">
                  Completed Users ({completedUsers.length})
                </Typography>
              </Box>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGridPremium
                  rows={completedUsers.map((user, index) => ({
                    id: user.userID || `completed-${index}`,
                    userName: user.userName,
                    email: user.email,
                    completionDate: user.courseCompletionDate,
                  }))}
                  columns={
                    [
                      {
                        field: 'userName',
                        headerName: 'Name',
                        flex: 1,
                        minWidth: 200,
                      },
                      {
                        field: 'completionDate',
                        headerName: 'Completed',
                        width: 150,
                        valueFormatter: (value) =>
                          value
                            ? dayjs.utc(value).local().format('MMM D, YYYY')
                            : '-',
                        renderCell: (params: GridRenderCellParams) =>
                          params.value
                            ? dayjs
                                .utc(params.value)
                                .local()
                                .format('MMM D, YYYY')
                            : '-',
                      },
                    ] as GridColDef[]
                  }
                  slots={{ toolbar: CourseProgressToolbar }}
                  showToolbar
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  disableRowSelectionOnClick
                  sx={dataGridSx}
                  localeText={{
                    noRowsLabel: searchQuery
                      ? 'No matching completed users'
                      : 'No users have completed this course yet',
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Box className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <Box className="flex items-center gap-2" sx={{ minHeight: 38 }}>
                  <UserIcon
                    sx={{ fontSize: 20, color: 'warning.main' }}
                    aria-hidden="true"
                  />
                  <Typography
                    color="warning.main"
                    variant="h6"
                    className="whitespace-nowrap"
                  >
                    Outstanding Users ({notCompletedUsers.length})
                  </Typography>
                </Box>
                {selectedUserIDs.length > 0 && (
                  <Box className="flex items-center gap-2">
                    <Chip
                      label={
                        selectedUserIDs.length === 1
                          ? `${selectedUserIDs.length} user selected`
                          : `${selectedUserIDs.length} users selected`
                      }
                      variant="outlined"
                      color="secondary"
                      size="medium"
                      className="whitespace-nowrap"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      className="whitespace-nowrap"
                      startIcon={<Check />}
                      onClick={handleGiveBulkCourseCredit}
                      disabled={isSubmitting}
                    >
                      Give Credit
                    </Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGridPremium
                  rows={notCompletedUsers.map((user, index) => ({
                    id: user.userID || `outstanding-${index}`,
                    userName: user.userName,
                    email: user.email,
                    assignmentProgress: user.assignmentProgress,
                    userID: user.userID,
                  }))}
                  columns={
                    [
                      {
                        field: 'userName',
                        headerName: 'Name',
                        flex: 1,
                        minWidth: 200,
                      },
                      {
                        field: 'actions',
                        headerName: 'Actions',
                        width: 140,
                        sortable: false,
                        renderCell: (params: GridRenderCellParams) => (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Check />}
                            disabled={isSubmitting}
                            onClick={() => {
                              setSelectedDataForCredit({
                                courseId: courseID!,
                                courseName: selectedCourse?.name || courseID!,
                                userId: params.row.userID,
                                userName: params.row.userName,
                              })
                              setCourseCreditModalOpen(true)
                            }}
                            aria-label={`Give credit for ${params.row.userName}`}
                          >
                            Give Credit
                          </Button>
                        ),
                      },
                    ] as GridColDef[]
                  }
                  slots={{ toolbar: CourseProgressToolbar }}
                  showToolbar
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  disableRowSelectionOnClick
                  checkboxSelection
                  rowSelectionModel={{
                    type: 'include',
                    ids: new Set(selectedUserIDs),
                  }}
                  onRowSelectionModelChange={(newSelection) =>
                    setSelectedUserIDs(Array.from(newSelection.ids) as string[])
                  }
                  sx={dataGridSx}
                  localeText={{
                    noRowsLabel: searchQuery
                      ? 'No matching incomplete users'
                      : 'All users have completed this course!',
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Card>
      )}
      <BulkCourseCreditModal
        isOpen={courseCreditModalOpen}
        courseGroups={
          selectedDataForCredit
            ? new Map([
                [
                  selectedDataForCredit.courseId,
                  {
                    courseID: selectedDataForCredit.courseId,
                    courseName: selectedDataForCredit.courseName,
                    userIDs: [selectedDataForCredit.userId],
                    userNames: [selectedDataForCredit.userName],
                  },
                ],
              ])
            : new Map()
        }
        totalUsers={1}
        isSubmitting={isSubmitting}
        onClose={() => {
          setSelectedDataForCredit(null)
          setCourseCreditModalOpen(false)
        }}
        onConfirm={handleGiveCourseCredit}
      />
      {NotificationComponent}
    </Container>
  )
}
